import { useApp } from '@/App';
import { DocumentItem } from '@/components/modals/DocumentModal';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import { LoadingOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useForm } from '@/libs/antd/form';
import { formatCurrency } from '@/libs/antd/formater';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import fetcher from '@/libs/fleet-api/fetcher';
import {
	getRequestSurveyDetail,
	RequestSurveyPayload,
} from '@/libs/fleet-api/services/prospect';
import { submitRequestSurvey } from '@/libs/fleet-api/services/survey';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	notification,
	Row,
	Table,
} from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment, useState } from 'react';
import { SurveyParticipant } from '@/libs/fleet-api/services/types';
import FleetModal from '@/libs/antd/components/Modal';
import GenericErrorMessage from '@/components/GenericErrorMessage';

export type FRMRequestSurveyFormProps = {
	prospectId: string;
};

type SurveyParticipantForm = {
	input: SurveyParticipant;
	items: SurveyParticipant[];
};

const FRMRequestSurveyForm: FC<FRMRequestSurveyFormProps> = ({
	prospectId,
}) => {
	const { modal } = useModals();
	const { form, value, fieldName } = useForm<RequestSurveyPayload>();
	const { navigate } = useApp();
	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openPraSurveyDataModal,
		openDocumentModal,
		openProspectDetail,
		openMapModal,
		openCustomerCheckingModal,
	} = useModals();

	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['getRequestSurveyDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getRequestSurveyDetail(id);
		},
	});

	const {
		approvalHistories,
		prospectInformation,
		mainInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
		surveyInformation,
	} = data?.data || {};

	const surveyParticipants = surveyInformation?.surveyParticipants ?? [];

	const participantForm = useForm<SurveyParticipantForm>();
	const [participantModal, setParticipantModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	// TODO: Set Real Value
	const customerInfoDataRow: DataRowItem[] = [
		[
			[
				'Nama Customer',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openCustomerDetail(mainInformation!.customerId);
					}}
				>
					{mainInformation?.customerName}
				</a>,
			],
			[
				'Prospect ID',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openProspectDetail(mainInformation!.prospectId);
					}}
				>
					{mainInformation?.prospectId}
				</a>,
			],
		],
		[
			['Kelas Industri', mainInformation?.industrySector?.industryClass],
			['Sumber Order', mainInformation?.orderSource],
		],
		[
			[
				'Portofolio Guideline',
				mainInformation?.industrySector?.portofolioGuideline,
			],
			[
				'Group Customer BMPP',
				mainInformation?.customerGroup?.id ? (
					<a
						href="#"
						className="trigger-popup"
						onClick={(e) => {
							e.preventDefault();
							openCustomerGroupDetail(mainInformation!.customerGroup.id);
						}}
						aria-disabled={!mainInformation?.customerGroup.id}
					>
						{mainInformation?.customerGroup.value ?? '-'}
					</a>
				) : (
					'-'
				),
			],
		],
		[
			[
				'Data Pra-Survey',
				<a
					href="#"
					onClick={(e) => {
						e.preventDefault();
						openPraSurveyDataModal(prospectId);
					}}
					className="trigger-popup"
				>
					Detail Pra-Survey
				</a>,
			],
			[
				'Analisa Customer Checking',
				<a
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						openCustomerCheckingModal(mainInformation!.prospectId);
					}}
				>
					Detail Analisa
				</a>,
			],
		],
		[
			[
				'Dokumen',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();

						const documents: DocumentItem[] = [];
						const getDocumentType = (name: string) => {
							if (name.endsWith('pdf')) return 'pdf';
							if (name.endsWith('jpeg')) return 'jpeg';
							if (name.endsWith('jpg')) return 'jpg';
							if (name.endsWith('png')) return 'png';
						};

						if (customerLegalDocuments && customerLegalDocuments.length) {
							customerLegalDocuments
								.filter((doc) => doc.file)
								.forEach((document) => {
									documents.push({
										name: document.documentType,
										type: getDocumentType(document.file.name)!,
										url: document.file.url!,
									});
								});
						}

						if (customerBusinessDocuments && customerBusinessDocuments.length) {
							customerBusinessDocuments
								.filter((doc) => doc.file)
								.forEach((document) => {
									documents.push({
										name: document.documentType,
										type: getDocumentType(document.file.name)!,
										url: document.file.url!,
									});
								});
						}
						if (customerReferralDocuments && customerReferralDocuments.length) {
							customerReferralDocuments
								.filter((doc) => doc.file)
								.forEach((document) => {
									documents.push({
										name: document.documentType,
										type: getDocumentType(document.file.name)!,
										url: document.file.url!,
									});
								});
						}

						if (documents.length) {
							openDocumentModal(documents);
						} else {
							alert('no document found');
						}
					}}
				>
					View Dokumen
				</a>,
			],
			[
				'SLIK Result',
				mainInformation?.slikResult
					? 'Kol ' + mainInformation?.slikResult
					: '-',
			],
		],
		[
			['FRM Name', mainInformation?.frm.value],
			['CA Name', mainInformation?.creditAnalyst.value],
		],
	];

	const submitRequestSurveyMutation = useMutation({
		mutationFn: submitRequestSurvey,
	});

	const handleSubmitRequestSurvey = async (values: RequestSurveyPayload) => {
		const isConfirmed = await modal.confirm({
			title: 'Konfirmasi Penginputan Survey',
			subTitle: 'Apakah data yang Anda inputkan sudah benar?',
		});

		if (!isConfirmed) return;

		const payload = {
			...values,
			participants: participantForm.value?.items?.map((item) => {
				if (item.isRequired === false) {
					item.id = null;
					item.userId = null;
					item.surveyId = null;
				}

				return item;
			}),
		} as RequestSurveyPayload;

		try {
			const { data } = await submitRequestSurveyMutation.mutateAsync(payload);
			if (data) {
				notification.success({
					message: 'Success',
					description: 'Survey Request has been submitted',
				});
				navigate('FRMRequestSurveyPage');
			}
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages &&
							message?.metadata?.debugInfo?.messages.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	if (isError) return <GenericErrorMessage />;

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Form.Provider
			onFormFinish={async (name, { values }) => {
				if (!name) return;
				handleSubmitRequestSurvey(values as RequestSurveyPayload);
			}}
		>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				items={customerInfoDataRow}
				style={{
					marginBottom: 20,
				}}
			/>

			<FleetTitle>Informasi Limit Pengajuan Pembiayaan Existing</FleetTitle>
			<Table
				pagination={false}
				scroll={{ x: 'max-content' }}
				loading={isLoading}
				bordered
				dataSource={
					prospectInformation?.existingFinancingLimitInformation.map(
						(record, key) => {
							return {
								...record,
								key,
							};
						},
					) ?? []
				}
				columns={[
					{
						title: 'No',
						render: (_, record) => record.no,
					},
					{
						title: 'No. MKL',
						render: (_, record) => record.napNo,
					},
					{
						title: 'Limit Pembiayaan Existing',
						render: (_, record) =>
							formatCurrency(record.existingFinancingLimit),
					},
					{
						title: 'Jenis Limit Pembiayaan',
						render: (_, record) => record.financingLimitType,
					},
					{
						title: 'Tipe Limit',
						render: (_, record) => record.financingLimitCharacteristic,
					},
					{
						title: 'Expired Date',
						render: (_, record) =>
							dayjs(record.expiredDate).format('DD/MM/YYYY'),
					},
					{
						title: 'NTF',
						render: (_, record) => formatCurrency(record.ntf) || 0,
					},
					{
						title: 'OS Principal',
						render: (_, record) => formatCurrency(record.outstandingPrincipal),
					},
					{
						title: 'Sisa Limit',
						render: (_, record) =>
							formatCurrency(record.outstandingFinancingLimit),
					},
					{
						title: 'Status',
						render: (_, record) => record.status,
					},
					// {
					// 	title: 'Select',
					// 	width: 1,
					// 	align: 'center',
					// 	render: () => (
					// 		<a style={{ display: 'block' }}>
					// 			<ArrowRightOutlined />
					// 		</a>
					// 	),
					// },
				]}
			/>
			<Divider />
			<FleetTitle>Informasi Limit Pengajuan Pembiayaan</FleetTitle>
			<Table
				bordered
				loading={isLoading}
				scroll={{ x: 'max-content' }}
				pagination={false}
				dataSource={
					prospectInformation?.financingApplicationLimitInformation.map(
						(record, key) => {
							return {
								...record,
								key,
							};
						},
					) ?? []
				}
				columns={[
					{
						title: 'No',
						align: 'center',
						render: (_, record) => record.no,
					},
					{
						title: 'Limit Pembiayaan Existing',
						render: (_, record) =>
							formatCurrency(record.existingFinancingLimit),
					},
					{
						title: 'Jenis Limit Pembiayaan',
						render: (_, record) => record.financingLimitType,
					},
					{
						title: 'OS Principal',
						render: (_, record) => formatCurrency(record.outstandingPrincipal),
					},
					{
						title: 'Tujuan Pembiayaan',
						render: (_, record) => record.financingPurpose,
					},
					{
						title: 'Pengajuan Limit',
						render: (_, record) => formatCurrency(record.limitApplication),
					},
					{
						title: 'Total Pengajuan Plafon',
						render: (_, record) => formatCurrency(record.totalLimitApplication),
					},
					{
						title: 'Segmen Market',
						render: (_, record) => record.segmentMarket,
					},
				]}
			/>
			<Divider />

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				name="surveyInfo"
			>
				<FleetTitle>Survey Information</FleetTitle>
				<Form.Item
					name={fieldName(['prospectId'])}
					initialValue={prospectId}
					noStyle
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Lokasi Survey Kantor"
							name={fieldName(['officeAddress'])}
							labelCol={{ span: 10 }}
							rules={[rule.required()]}
							initialValue={surveyInformation?.officeAddress}
						>
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openMapModal({
										title: 'Lokasi Survey Kantor',
										address: value?.officeAddress,
										draggable: true,
										onChange: ({ center, address }) => {
											form.setFieldValue(fieldName('officeLat'), center.lat);
											form.setFieldValue(fieldName('officeLong'), center.lng);
											form.setFieldValue(fieldName('officeAddress'), address);
										},
									});
								}}
							>
								{value?.officeAddress ?? 'Pilih Lokasi Kantor'}
							</a>
						</Form.Item>
						<Form.Item
							name={fieldName(['officeLat'])}
							initialValue={surveyInformation?.officeLat}
							noStyle
						/>
						<Form.Item
							name={fieldName(['officeLong'])}
							initialValue={surveyInformation?.officeLong}
							noStyle
						/>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Tanggal Survey Kantor"
							name={fieldName(['surveyOfficeDate'])}
							labelCol={{ span: 10 }}
							rules={[rule.required()]}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							initialValue={surveyInformation?.surveyOfficeDate}
						>
							<DatePicker
								disabledDate={(now) => now < dayjs().subtract(1, 'day')}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Lokasi Survey Site"
							name={fieldName(['siteAddress'])}
							labelCol={{ span: 10 }}
							rules={[rule.required()]}
							initialValue={surveyInformation?.siteAddress}
						>
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openMapModal({
										title: 'Lokasi Survey Site',
										address: value?.siteAddress,
										draggable: true,
										onChange: ({ center, address }) => {
											form.setFieldValue(fieldName('siteLat'), center.lat);
											form.setFieldValue(fieldName('siteLong'), center.lng);
											form.setFieldValue(fieldName('siteAddress'), address);
										},
									});
								}}
							>
								{value?.siteAddress ?? 'Pilih Lokasi Site'}
							</a>
						</Form.Item>
						<Form.Item
							name={fieldName(['siteLat'])}
							initialValue={surveyInformation?.siteLat}
							noStyle
						/>
						<Form.Item
							name={fieldName(['siteLong'])}
							initialValue={surveyInformation?.siteLong}
							noStyle
						/>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Tanggal Survey Site"
							name={fieldName(['surveySiteDate'])}
							labelCol={{ span: 10 }}
							rules={[rule.required()]}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							initialValue={surveyInformation?.surveySiteDate}
						>
							<DatePicker
								disabledDate={(now) => now < dayjs().subtract(1, 'day')}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
			</Form>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={participantForm.form}
				initialValues={
					{
						items: surveyParticipants ?? [],
					} as SurveyParticipantForm
				}
				onFinish={async ({ input, items }) => {
					input.isRequired = false;
					if (participantModal?.type === 'edit') {
						participantForm.form.setFieldsValue({
							items: items.map((list, index) => {
								return index === participantModal.index ? input! : list;
							}),
						});
					} else {
						participantForm.form.setFieldsValue({
							items: [...items, input],
						});
					}
					setParticipantModal(null);
				}}
			>
				<Form.Item
					name={participantForm.fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={participantForm.fieldName(['items'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as SurveyParticipantForm;
						const dataSource =
							values.items?.map((record, key) => {
								return {
									...record,
									key,
								};
							}) ?? [];

						return (
							<Fragment>
								<Row style={{ display: 'flex', justifyContent: 'end' }}>
									<Button
										style={{
											color: '#1557BC',
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											borderColor: '#1557BC',
											fontWeight: 700,
											marginBottom: 20,
										}}
										icon={'+'}
										onClick={() => {
											setParticipantModal({ type: 'add' });
										}}
									>
										Add Another
									</Button>
								</Row>

								<Table
									bordered
									loading={isLoading}
									pagination={false}
									dataSource={dataSource}
									columns={[
										{
											title: 'No',
											width: 1,
											align: 'center',
											render: (_, _record, index) => index + 1,
										},
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											width: 1,
											align: 'center',
											render: (_, record, index) => {
												if (record.isRequired !== false) return '-';
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(
																		participantForm.fieldName(['input']),
																		record,
																	);
																	setParticipantModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete = await modal.confirm({
																		title: 'Delete this data?',
																	});

																	if (!confirmDelete) return;
																	participantForm.form.setFieldsValue({
																		items: values.items.filter(
																			(_, listIndex) => listIndex !== index,
																		),
																	});
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
										},
										{
											title: 'Nama Peserta Survey',
											align: 'center',
											render: (record) => record.name ?? '-',
										},
										{
											title: 'Jabatan',
											render: (record) => record.position ?? '-',
										},
									]}
								/>

								<FleetModal
									open={Boolean(participantModal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										participantForm.form.resetFields([
											participantForm.fieldName('input'),
										]);
									}}
									onCancel={() => {
										setParticipantModal(null);
									}}
									onOk={() => {
										participantForm.form.submit();
									}}
								>
									<FleetTitle>Add Participant</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={participantForm.fieldName(['input', 'userId'])}
												noStyle
											/>
											<Form.Item
												name={participantForm.fieldName(['input', 'name'])}
												label="Nama"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={participantForm.fieldName(['input', 'position'])}
												label="Jabatan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
			<Divider />

			{approvalHistories && approvalHistories.length > 0 && (
				<Fragment>
					<FleetTitle>Approval History</FleetTitle>
					<Table
						bordered
						loading={isLoading}
						pagination={false}
						dataSource={
							approvalHistories?.map((approval, key) => {
								return {
									key,
									...approval,
								};
							}) ?? []
						}
						columns={[
							{
								title: 'No',
								render: (_, _record, index) => index + 1,
							},
							{
								title: 'Name',
								render: (_, record) => record.approvalBy,
							},
							{
								title: 'Date',
								render: (_, record) => record.approvalDate,
							},
							{
								title: 'Process',
								render: (_, record) => record.approvalProcess,
							},
							{
								title: 'Status',
								render: (_, record) => record.approvalStatus,
							},
							{
								title: 'Status',
								align: 'center',
								render: (_, record) => {
									return (
										<span
											className={`review-status ${record.approvalStatus.toLowerCase()}`}
										>
											{record.approvalStatus}
										</span>
									);
								},
							},
							{
								title: 'Approval Notes',
								render: (_, record) => record.approvalNote,
							},
						]}
						style={{ marginBottom: 20 }}
					/>
				</Fragment>
			)}

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				name="notes"
			>
				<Row gutter={[40, 20]}>
					<Col span={24}>
						<Form.Item
							label="Request Notes"
							name={fieldName(['notes'])}
							labelCol={{ span: 4 }}
							rules={[rule.required()]}
						>
							<Input.TextArea rows={3} />
						</Form.Item>
					</Col>
				</Row>

				<Button
					type="primary"
					htmlType="submit"
					loading={isLoading || submitRequestSurveyMutation.isPending}
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 60,
					}}
				>
					SAVE
				</Button>
			</Form>
		</Form.Provider>
	);
};

export default FRMRequestSurveyForm;
